import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Pause, Square, Circle, Tag, ChevronDown, Clock, Play } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ClientPicker from "@/components/ClientPicker";
import { localIso } from "@/lib/date";

// Iter 112 — TimerWidget rebuilt for cross-device sync + running-context
// chip + Today preview. The server is now the source of truth
// (GET/PUT/DELETE /api/me/active-timer). localStorage is retained as an
// offline cache so the widget renders instantly on page load and can
// survive brief network dips without visually resetting.

const CACHE_KEY = "close_timer_state_v2";

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || null; }
  catch { return null; }
};
const writeCache = (s) => {
  if (!s) localStorage.removeItem(CACHE_KEY);
  else localStorage.setItem(CACHE_KEY, JSON.stringify(s));
};

const fmt = (totalMs) => {
  const s = Math.max(0, Math.floor(totalMs / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const contextLabel = (t) => {
  if (!t) return "";
  const parts = [t.client_name, t.period_label, t.task_name].filter(Boolean);
  return parts.join(" · ");
};

export default function TimerWidget() {
  const [state, setState] = useState(readCache());
  const [today, setToday] = useState(null);
  const [tick, setTick] = useState(0);
  const [stopOpen, setStopOpen] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [logForm, setLogForm] = useState({ client_id: "", period_id: "", task_id: "", notes: "", billable: true });
  const intervalRef = useRef(null);
  const pushTimeoutRef = useRef(null);

  // ---- server sync helpers ------------------------------------------------
  const pullFromServer = useCallback(async () => {
    try {
      const r = await api.get("/me/active-timer");
      if (r.data?.active) {
        setState((prev) => {
          const next = { ...r.data.timer };
          // Prefer server timestamps but keep local `_optimistic` marker off.
          writeCache(next);
          return next;
        });
      } else {
        setState((prev) => {
          if (prev) writeCache(null);
          return null;
        });
      }
    } catch { /* offline — keep local */ }
  }, []);

  const pushToServer = useCallback(async (next) => {
    // Debounce rapid tick-driven writes.
    if (pushTimeoutRef.current) clearTimeout(pushTimeoutRef.current);
    pushTimeoutRef.current = setTimeout(async () => {
      try {
        if (!next) {
          await api.delete("/me/active-timer");
        } else {
          const r = await api.put("/me/active-timer", {
            status: next.status,
            started_at: next.started_at,
            accumulated_ms: next.accumulated_ms || 0,
            client_id: next.client_id || null,
            period_id: next.period_id || null,
            task_id: next.task_id || null,
            notes: next.notes || "",
          });
          // The server response hydrates client_name/period_label/task_name/section.
          // Consume it immediately so the context chip label renders without
          // waiting for the next 20s poll.
          if (r?.data?.timer) {
            setState((prev) => {
              // Preserve any newer local ticks (elapsed is derived, not stored).
              const hydrated = { ...r.data.timer };
              writeCache(hydrated);
              return hydrated;
            });
          }
        }
      } catch (e) { /* silent — cache holds it */ }
    }, 150);
  }, []);

  const persist = useCallback((next) => {
    setState(next);
    writeCache(next);
    pushToServer(next);
  }, [pushToServer]);

  // ---- lifecycle ----------------------------------------------------------
  // Initial pull + poll every 20s + refresh on window focus.
  useEffect(() => {
    pullFromServer();
    const iv = setInterval(pullFromServer, 20000);
    const onFocus = () => pullFromServer();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [pullFromServer]);

  // Tick when running
  useEffect(() => {
    if (state?.status === "running") {
      intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [state?.status]);

  // Cross-tab sync via localStorage change
  useEffect(() => {
    const onStorage = (e) => { if (e.key === CACHE_KEY) setState(readCache()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Load today summary whenever preview opens (kept out of hot path).
  useEffect(() => {
    if (!previewOpen) return;
    api.get("/me/today-time-summary").then((r) => setToday(r.data)).catch(() => {});
  }, [previewOpen]);

  // Periods/tasks 403 for clients outside the user's team — treat as empty.
  const loadPeriods = (cid) => cid
    ? api.get(`/periods?client_id=${cid}`).then((r) => setPeriods(r.data || [])).catch(() => setPeriods([]))
    : Promise.resolve(setPeriods([]));
  const loadTasks = (pid) => pid
    ? api.get(`/periods/${pid}/tasks`).then((r) => setTasks(r.data || [])).catch(() => setTasks([]))
    : Promise.resolve(setTasks([]));

  // Hydrate the period/task chain when the context panel opens.
  useEffect(() => {
    if (!ctxOpen) return;
    loadPeriods(state?.client_id);
    loadTasks(state?.period_id);
  }, [ctxOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- elapsed calc -------------------------------------------------------
  const elapsedMs = (() => {
    if (!state) return 0;
    const base = state.accumulated_ms || 0;
    if (state.status === "running" && state.started_at) {
      return base + (Date.now() - state.started_at);
    }
    return base;
  })();
  // Touch tick so the running-clock re-renders every second.
  void tick;

  // ---- actions ------------------------------------------------------------
  const start = () => {
    const now = Date.now();
    const next = state?.status === "paused"
      ? { ...state, status: "running", started_at: now }
      : { status: "running", started_at: now, accumulated_ms: 0,
          client_id: state?.client_id || null,
          period_id: state?.period_id || null,
          task_id: state?.task_id || null,
          notes: state?.notes || "" };
    persist(next);
  };

  const pause = () => {
    if (!state || state.status !== "running") return;
    const acc = (state.accumulated_ms || 0) + (Date.now() - state.started_at);
    persist({ ...state, status: "paused", started_at: null, accumulated_ms: acc });
  };

  const stop = () => {
    if (!state) return;
    // Pre-fill the log dialog from whatever context was already attached.
    setLogForm({
      client_id: state.client_id || "",
      period_id: state.period_id || "",
      task_id: state.task_id || "",
      notes: state.notes || "",
      billable: true,
    });
    // Also chain the dependent selects.
    loadPeriods(state.client_id);
    loadTasks(state.period_id);
    setStopOpen(true);
  };

  const onPickClient = async (cid) => {
    setLogForm((f) => ({ ...f, client_id: cid, period_id: "", task_id: "" }));
    setTasks([]);
    await loadPeriods(cid);
  };

  const onPickPeriod = async (pid) => {
    setLogForm((f) => ({ ...f, period_id: pid, task_id: "" }));
    await loadTasks(pid);
  };

  const confirmStop = async () => {
    const hours = Math.max(0.01, Math.round((elapsedMs / 3600000) * 100) / 100);
    try {
      const end = new Date();
      const start = new Date(end.getTime() - elapsedMs);
      const hm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      const sameDay = start.toDateString() === end.toDateString();
      await api.post("/time-entries", {
        date: localIso(end),
        hours,
        start_time: sameDay ? hm(start) : null,
        end_time: sameDay ? hm(end) : null,
        client_id: logForm.client_id || null,
        period_id: logForm.period_id || null,
        task_id: logForm.task_id || null,
        notes: logForm.notes,
        billable: logForm.billable,
      });
      toast.success(`Logged ${hours.toFixed(2)}h`);
      persist(null);
      setStopOpen(false);
      setLogForm({ client_id: "", period_id: "", task_id: "", notes: "", billable: true });
      // Refresh preview if it happens to be open.
      if (previewOpen) {
        api.get("/me/today-time-summary").then((r) => setToday(r.data)).catch(() => {});
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const discard = () => {
    if (!window.confirm("Discard this timer without logging?")) return;
    persist(null);
    setStopOpen(false);
  };

  // Attach or replace the running-timer context.
  const attachContext = async ({ client_id, period_id, task_id }) => {
    if (!state) return;
    const next = { ...state, client_id: client_id || null, period_id: period_id || null, task_id: task_id || null };
    // Immediate persist so cross-tab + backend picks up the label.
    persist(next);
    await Promise.all([loadPeriods(client_id), loadTasks(period_id)]);
  };

  // Continue a past entry — start a fresh timer inheriting the entry's context.
  const continueEntry = (e) => {
    const next = {
      status: "running",
      started_at: Date.now(),
      accumulated_ms: 0,
      client_id: e.client_id || null,
      period_id: e.period_id || null,
      task_id: e.task_id || null,
      notes: e.notes || "",
    };
    persist(next);
    setPreviewOpen(false);
    toast.success(`Timer started · ${contextLabel(e) || "no context"}`);
  };

  const isRunning = state?.status === "running";
  const isPaused = state?.status === "paused";
  const isIdle = !state;
  const ctxLabel = contextLabel(state);

  return (
    <>
      <div
        className={`flex items-center gap-1.5 border rounded-md px-2 py-1 transition-colors ${
          isRunning ? "bg-red-50 border-red-200" : isPaused ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"
        }`}
        data-testid="timer-widget"
      >
        {/* Status pill + elapsed. Clicking the elapsed opens the Today preview. */}
        <Popover open={previewOpen} onOpenChange={setPreviewOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="timer-preview-trigger"
              className={`flex items-center gap-2 px-1.5 py-0.5 rounded hover:bg-slate-100 ${
                isRunning ? "text-red-700 hover:bg-red-100" : isPaused ? "text-amber-700 hover:bg-amber-100" : "text-slate-500"
              }`}
              title="Today's time · click for details"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${isRunning ? "bg-red-500 animate-pulse" : isPaused ? "bg-amber-500" : "bg-slate-300"}`}/>
              <span className="font-mono text-sm tabular-nums" data-testid="timer-display">{fmt(elapsedMs)}</span>
              {ctxLabel && (
                <span className="hidden lg:inline text-[11px] max-w-[180px] truncate opacity-80" data-testid="timer-context-label" title={ctxLabel}>
                  · {ctxLabel}
                </span>
              )}
              <ChevronDown className="h-3 w-3 opacity-60"/>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="timer-preview-panel">
            <TodayPreview
              today={today}
              activeState={state}
              elapsedMs={elapsedMs}
              onContinue={continueEntry}
            />
          </PopoverContent>
        </Popover>

        {/* Running/Paused → attach or edit context via popover. */}
        {!isIdle && (
          <Popover open={ctxOpen} onOpenChange={setCtxOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="timer-context-btn"
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px] gap-1 bg-white shrink-0"
                title={ctxLabel ? "Change context" : "Attach context"}
              >
                <Tag className="h-3 w-3"/>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 space-y-2" data-testid="timer-context-panel">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                Track this timer for…
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-[11px] text-slate-500">Client</Label>
                  <ClientPicker
                    value={state.client_id || ""}
                    onChange={(cid) => attachContext({ client_id: cid })}
                    testIdPrefix="timer-context-client"
                  />
                </div>
                {state.client_id && periods.length > 0 && (
                  <div>
                    <Label className="text-[11px] text-slate-500">Close period</Label>
                    <Select
                      value={state.period_id || "none"}
                      onValueChange={(v) => attachContext({ client_id: state.client_id, period_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="timer-context-period"><SelectValue placeholder="No period"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No period</SelectItem>
                        {periods.map((p) => <SelectItem key={p.period_id} value={p.period_id}>{p.period_label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {state.period_id && (
                  <div>
                    <Label className="text-[11px] text-slate-500">Task</Label>
                    <Select
                      value={state.task_id || "none"}
                      onValueChange={(v) => attachContext({ client_id: state.client_id, period_id: state.period_id, task_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid="timer-context-task"><SelectValue placeholder="No task"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No task</SelectItem>
                        {tasks.map((t) => <SelectItem key={t.task_id} value={t.task_id}>{t.section} · {t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {ctxLabel && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] text-slate-500 hover:text-red-600 w-full"
                    onClick={() => attachContext({ client_id: "", period_id: "", task_id: "" })}
                    data-testid="timer-context-clear"
                  >
                    Clear context
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {!isRunning && (
          <Button
            data-testid="timer-start"
            size="sm"
            onClick={start}
            className="h-7 px-2 bg-red-600 hover:bg-red-700 text-white shrink-0"
            title={isPaused ? "Resume timer" : "Start timer"}
          >
            <Circle className="h-3 w-3 fill-current"/>
          </Button>
        )}
        {isRunning && (
          <Button data-testid="timer-pause" size="sm" variant="outline" onClick={pause} className="h-7 px-2 bg-white shrink-0" title="Pause"><Pause className="h-3 w-3"/></Button>
        )}
        {!isIdle && (
          <Button
            data-testid="timer-stop"
            size="sm"
            variant="outline"
            onClick={stop}
            className="h-7 px-2 bg-white shrink-0"
            title="Stop &amp; log"
          ><Square className="h-3 w-3"/></Button>
        )}
      </div>

      <Dialog open={stopOpen} onOpenChange={setStopOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Log {(elapsedMs / 3600000).toFixed(2)} hours</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Client</Label>
              <ClientPicker
                value={logForm.client_id}
                onChange={(cid) => onPickClient(cid)}
                testIdPrefix="timer-client"
              />
            </div>
            {logForm.client_id && periods.length > 0 && (
              <div>
                <Label className="text-sm">Close period</Label>
                <Select value={logForm.period_id || "none"} onValueChange={(v)=>onPickPeriod(v==="none"?"":v)}>
                  <SelectTrigger><SelectValue placeholder="No period"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No period</SelectItem>
                    {periods.map((p)=><SelectItem key={p.period_id} value={p.period_id}>{p.period_label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {logForm.period_id && (
              <div>
                <Label className="text-sm">Task</Label>
                <Select value={logForm.task_id || "none"} onValueChange={(v)=>setLogForm({...logForm, task_id:v==="none"?"":v})}>
                  <SelectTrigger><SelectValue placeholder="No task"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No task</SelectItem>
                    {tasks.map((t)=><SelectItem key={t.task_id} value={t.task_id}>{t.section} · {t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-sm">Notes</Label><Textarea rows={2} data-testid="timer-notes" value={logForm.notes} onChange={(e)=>setLogForm({...logForm, notes:e.target.value})} placeholder="What did you work on?"/></div>
          </div>
          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
            <Button variant="ghost" className="text-red-600 hover:text-red-700" data-testid="timer-discard" onClick={discard}>Discard</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>setStopOpen(false)}>Cancel</Button>
              <Button data-testid="timer-log" onClick={confirmStop} className="bg-slate-900 hover:bg-slate-800 text-white">Log time</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// -----------------------------------------------------------------------
// Today preview panel — shown when the user clicks the elapsed chip.
// Surfaces total hours logged today, the current running-timer context,
// and the three most recent entries with a one-click Continue button.
// -----------------------------------------------------------------------
function TodayPreview({ today, activeState, elapsedMs, onContinue }) {
  const runningLabel = contextLabel(activeState);
  return (
    <div className="p-3 space-y-3" data-testid="timer-preview-content">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-1.5 text-slate-900">
          <Clock className="h-3.5 w-3.5 text-slate-500"/>
          <span className="text-sm font-display tracking-tight">Today</span>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {today ? `${today.total_hours.toFixed(2)}h logged` : "loading…"}
        </div>
      </div>

      {activeState && (
        <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] flex items-center justify-between gap-2" data-testid="timer-preview-running">
          <div className="min-w-0">
            <div className="text-red-700 uppercase tracking-wider text-[10px]">Running</div>
            <div className="truncate text-slate-900 font-medium" title={runningLabel || "no context"}>
              {runningLabel || "No context attached"}
            </div>
          </div>
          <div className="font-mono tabular-nums text-red-700 shrink-0">{fmt(elapsedMs)}</div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Recent entries</div>
        {!today || today.recent?.length === 0 ? (
          <div className="text-[11px] text-slate-400 italic py-2 text-center border border-dashed border-slate-200 rounded" data-testid="timer-preview-empty">
            No entries logged today yet.
          </div>
        ) : (
          <ul className="space-y-1" data-testid="timer-preview-recent">
            {today.recent.map((e) => {
              const label = contextLabel(e) || (e.notes ? e.notes.slice(0, 40) : "Untitled entry");
              return (
                <li key={e.entry_id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 group">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs truncate text-slate-900" title={label}>{label}</div>
                    {e.notes && <div className="text-[10px] text-slate-400 truncate" title={e.notes}>{e.notes}</div>}
                  </div>
                  <div className="text-[11px] tabular-nums text-slate-500 shrink-0">{Number(e.hours).toFixed(2)}h</div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => onContinue(e)}
                    title="Start a new timer with this context"
                    data-testid={`timer-preview-continue-${e.entry_id}`}
                  >
                    <Play className="h-3 w-3"/>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="pt-1 border-t border-slate-100 flex items-center justify-between text-[11px]">
        <span className="text-slate-400">
          {today ? `${today.entry_count} ${today.entry_count === 1 ? "entry" : "entries"}` : ""}
        </span>
        <Link to="/timesheet" className="text-blue-600 hover:underline" data-testid="timer-preview-timesheet-link">
          Open timesheet →
        </Link>
      </div>
    </div>
  );
}
